import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { useApi } from "@/lib/api";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";

interface HasId {
  id: string;
}

export function useEntityList<TEntity extends HasId>(endpointSegment: string) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const [items, setItems] = useState<TEntity[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilterValue] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [statusFilter, setStatusFilterValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  function setNameFilter(value: string) {
    setNameFilterValue(value);
    setPage(1);
  }

  function setStatusFilter(value: string) {
    setStatusFilterValue(value);
    setPage(1);
  }

  function clearFilters() {
    setNameFilterValue("");
    setStatusFilterValue("");
    setPage(1);
  }

  function retry() {
    setError(null);
    setReloadToken((token) => token + 1);
  }

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadItems() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedNameFilter) {
        params.set("name", debouncedNameFilter);
      }

      if (statusFilter) {
        params.set("status", statusFilter);
      }

      const data = await api.get<PagedResult<TEntity>>(
        `/organizations/${orgId}/${endpointSegment}?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      if (data) {
        setItems(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      } else {
        setError(`Couldn't load ${endpointSegment}.`);
      }
    }

    void loadItems();

    const interval = setInterval(() => void loadItems(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    accessToken,
    api,
    orgId,
    endpointSegment,
    page,
    debouncedNameFilter,
    statusFilter,
    reloadToken,
  ]);

  return {
    items,
    totalCount,
    page,
    setPage,
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    clearFilters,
    retry,
    error,
    hasFilters: Boolean(debouncedNameFilter || statusFilter),
  };
}
